import type { AddUserFormData, FormErrors, SalesDistrict } from './addUserTypes';

export type PasswordFieldFlags = {
    showEncryptedPassword: boolean;
    showPassword: boolean;
};

export function clearFieldErrorIfPresent(
    errors: FormErrors,
    name: string
): FormErrors | null {
    if (!errors[name as keyof FormErrors]) {
        return null;
    }
    return { ...errors, [name]: '' };
}

export function applyPhoneCodeChange(
    prev: AddUserFormData,
    name: 'mobilePhoneCode' | 'officePhoneCode',
    value: string
): AddUserFormData | null {
    const numericValue = value.replace(/\D/g, '');
    if (numericValue.length > 3) {
        return null;
    }
    return { ...prev, [name]: numericValue };
}

export function applySsoToggleChange(prev: AddUserFormData, enabled: boolean): AddUserFormData {
    return {
        ...prev,
        ssoEnabled: enabled,
        password: '',
    };
}

export function applySalesDistrictChange(
    prev: AddUserFormData,
    value: string,
    districts: SalesDistrict[]
): AddUserFormData {
    const selectedDistrict = districts.find(d => d.name === value);
    return {
        ...prev,
        salesDistrict: value,
        salesRegion: selectedDistrict ? selectedDistrict.salesRegion : '',
    };
}

export function mapApiUserToFormData(userData: Record<string, unknown>): AddUserFormData {
    return {
        userType: (userData.type as AddUserFormData['userType']) || 'Internal',
        email: (userData.email as string) || '',
        password: '',
        ssoEnabled: Boolean(userData.isSsoEnabled),
        firstName: (userData.firstName as string) || '',
        lastName: (userData.lastName as string) || '',
        mobilePhoneCode: (userData.mobilePhoneCode as string) || '',
        mobilePhone: (userData.mobilePhoneNo as string) || '',
        officePhoneCode: (userData.officePhoneCode as string) || '',
        officePhone: (userData.officePhoneNo as string) || '',
        role: (userData.role as string) || 'User',
        status: (userData.status as AddUserFormData['status']) || 'Enabled',
        customerId: (userData.customerId as string) || '',
        salesDistrict: (userData.salesDistrict as string) || '',
        salesRegion: (userData.salesRegion as string) || '',
    };
}

export function accessVisibilityToSelectionMap(accessVisibility: unknown): Record<string, boolean> {
    if (!Array.isArray(accessVisibility)) {
        return {};
    }
    const selectedPlants: Record<string, boolean> = {};
    for (const plantId of accessVisibility) {
        selectedPlants[plantId as string] = true;
    }
    return selectedPlants;
}

export type AddUserFormDataUpdate =
    | { kind: 'skip' }
    | { kind: 'set'; formData: AddUserFormData };

export function resolveAddUserFormDataChange(
    name: string,
    value: string,
    prev: AddUserFormData,
    districts: SalesDistrict[]
): AddUserFormDataUpdate {
    if (name === 'mobilePhoneCode' || name === 'officePhoneCode') {
        const next = applyPhoneCodeChange(prev, name, value);
        return next ? { kind: 'set', formData: next } : { kind: 'skip' };
    }

    if (name === 'ssoEnabled') {
        return { kind: 'set', formData: applySsoToggleChange(prev, value as unknown as boolean) };
    }

    if (name === 'salesDistrict') {
        return { kind: 'set', formData: applySalesDistrictChange(prev, value, districts) };
    }

    return { kind: 'set', formData: { ...prev, [name]: value } };
}

export function shouldResetPasswordDisplayOnInput(name: string): boolean {
    return name === 'password' || name === 'ssoEnabled';
}
